import React, { useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Animated,
  Platform,
  Linking,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import {
  getClockStatus,
  initNfc,
  nfcClockAction,
  cancelNfcScan,
  resetNfcStatus,
  NfcStatus,
} from '@/store/slices/timeClockSlice';
import nfcService from '@/services/nfcService';

const NfcClockScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    clockStatus,
    nfcSupported,
    nfcEnabled,
    nfcStatus,
    nfcLastAction,
    nfcError,
    isLoading,
  } = useSelector((state: RootState) => state.timeClock);

  const pulseAnim = React.useRef(new Animated.Value(1)).current;

  // Initialize NFC on mount
  useEffect(() => {
    dispatch(initNfc());
    dispatch(getClockStatus());
  }, [dispatch]);

  // Pulse animation while scanning
  useEffect(() => {
    if (nfcStatus === 'scanning') {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.15,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    } else {
      pulseAnim.setValue(1);
    }
  }, [nfcStatus, pulseAnim]);

  // Auto-reset success/error status after a delay
  useEffect(() => {
    if (nfcStatus === 'success' || nfcStatus === 'error') {
      const timer = setTimeout(() => {
        dispatch(resetNfcStatus());
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [nfcStatus, dispatch]);

  // Show error alert
  useEffect(() => {
    if (nfcError) {
      Alert.alert('NFC Error', nfcError);
    }
  }, [nfcError]);

  const handleTapToScan = useCallback(() => {
    if (nfcStatus === 'scanning') {
      dispatch(cancelNfcScan());
      return;
    }
    dispatch(nfcClockAction());
  }, [dispatch, nfcStatus]);

  const handleOpenNfcSettings = () => {
    if (Platform.OS === 'android') {
      nfcService.goToNfcSettings();
    } else {
      Linking.openSettings();
    }
  };

  const isClockedIn = clockStatus?.clockedIn || false;

  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTime = (dateString: string): string => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Render NFC not supported
  if (!nfcSupported) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <Icon name="close-circle-outline" size={80} color="#FF3B30" />
          <Text style={styles.title}>NFC Not Available</Text>
          <Text style={styles.subtitle}>
            Your device does not support NFC. Use the Time Clock tab to clock in and out manually.
          </Text>
        </View>
      </View>
    );
  }

  // Render NFC disabled
  if (!nfcEnabled) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <Icon name="wifi-outline" size={80} color="#FF9500" />
          <Text style={styles.title}>NFC is Disabled</Text>
          <Text style={styles.subtitle}>
            Enable NFC in your device settings to use tap-to-clock.
          </Text>
          <TouchableOpacity style={styles.settingsButton} onPress={handleOpenNfcSettings}>
            <Text style={styles.settingsButtonText}>Open NFC Settings</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => dispatch(initNfc())}
          >
            <Text style={styles.retryButtonText}>Check Again</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Current Status Banner */}
      <View style={[styles.statusBanner, isClockedIn ? styles.statusBannerIn : styles.statusBannerOut]}>
        <View style={[styles.statusDot, { backgroundColor: isClockedIn ? '#34C759' : '#FF3B30' }]} />
        <Text style={styles.statusBannerText}>
          {isClockedIn ? 'Currently Clocked In' : 'Currently Clocked Out'}
        </Text>
        {isClockedIn && clockStatus && (
          <Text style={styles.statusBannerDetail}>
            {formatMinutes(clockStatus.minutesWorked)} worked
          </Text>
        )}
      </View>

      {/* Main NFC Tap Area */}
      <View style={styles.centerContent}>
        {nfcStatus === 'success' ? (
          <SuccessView action={nfcLastAction} />
        ) : (
          <>
            <Text style={styles.instruction}>
              {nfcStatus === 'scanning'
                ? Platform.OS === 'ios'
                  ? 'Hold the top of your iPhone near the NFC tag...'
                  : 'Hold your phone near the NFC tag...'
                : 'Tap the button, then hold your phone near an IN or OUT tag'}
            </Text>

            <TouchableOpacity
              activeOpacity={0.8}
              onPress={handleTapToScan}
              disabled={isLoading}
            >
              <Animated.View
                style={[
                  styles.nfcCircle,
                  nfcStatus === 'scanning' && styles.nfcCircleScanning,
                  { transform: [{ scale: pulseAnim }] },
                ]}
              >
                <Icon
                  name={nfcStatus === 'scanning' ? 'radio-outline' : 'scan-outline'}
                  size={64}
                  color="#FFFFFF"
                />
                <Text style={styles.nfcCircleText}>
                  {nfcStatus === 'scanning' ? 'Scanning...' : 'Tap to Scan'}
                </Text>
              </Animated.View>
            </TouchableOpacity>

            {nfcStatus === 'scanning' && (
              <TouchableOpacity style={styles.cancelButton} onPress={() => dispatch(cancelNfcScan())}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </View>

      {/* Info Section */}
      <View style={styles.infoSection}>
        <View style={styles.infoRow}>
          <View style={styles.infoItem}>
            <View style={[styles.tagIcon, { backgroundColor: '#34C759' }]}>
              <Icon name="arrow-forward" size={16} color="#FFFFFF" />
            </View>
            <Text style={styles.infoLabel}>IN tag</Text>
            <Text style={styles.infoDetail}>Clock in</Text>
          </View>
          <View style={styles.infoItem}>
            <View style={[styles.tagIcon, { backgroundColor: '#FF3B30' }]}>
              <Icon name="arrow-back" size={16} color="#FFFFFF" />
            </View>
            <Text style={styles.infoLabel}>OUT tag</Text>
            <Text style={styles.infoDetail}>Clock out</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const SuccessView: React.FC<{ action: string | null }> = ({ action }) => {
  const isClockIn = action === 'CLOCK_IN';
  return (
    <View style={styles.successContainer}>
      <View style={[styles.successCircle, isClockIn ? styles.successIn : styles.successOut]}>
        <Icon name="checkmark-circle" size={80} color="#FFFFFF" />
      </View>
      <Text style={styles.successText}>
        {isClockIn ? 'Clocked In!' : 'Clocked Out!'}
      </Text>
      <Text style={styles.successTime}>
        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F8F8',
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
    marginTop: 16,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#666666',
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 22,
  },
  instruction: {
    fontSize: 16,
    color: '#666666',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 22,
  },
  statusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
  },
  statusBannerIn: {
    backgroundColor: '#E8F8ED',
  },
  statusBannerOut: {
    backgroundColor: '#FDE8E8',
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 10,
  },
  statusBannerText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333333',
    flex: 1,
  },
  statusBannerDetail: {
    fontSize: 14,
    color: '#666666',
  },
  nfcCircle: {
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 8,
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
  },
  nfcCircleScanning: {
    backgroundColor: '#FF9500',
  },
  nfcCircleText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 8,
  },
  cancelButton: {
    marginTop: 24,
    padding: 12,
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#FF3B30',
    fontWeight: '600',
  },
  settingsButton: {
    marginTop: 24,
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 10,
  },
  settingsButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  retryButton: {
    marginTop: 12,
    padding: 12,
  },
  retryButtonText: {
    fontSize: 14,
    color: '#007AFF',
  },
  infoSection: {
    padding: 20,
    paddingBottom: 32,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 32,
  },
  infoItem: {
    alignItems: 'center',
  },
  tagIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 6,
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333333',
  },
  infoDetail: {
    fontSize: 12,
    color: '#999999',
  },
  successContainer: {
    alignItems: 'center',
  },
  successCircle: {
    width: 160,
    height: 160,
    borderRadius: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  successIn: {
    backgroundColor: '#34C759',
  },
  successOut: {
    backgroundColor: '#FF3B30',
  },
  successText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333333',
    marginTop: 20,
  },
  successTime: {
    fontSize: 20,
    color: '#666666',
    marginTop: 8,
  },
});

export default NfcClockScreen;
